import { useEffect, useMemo, useState } from 'react';
import {
  createPayout,
  createPayoutBatch,
  getPayout,
  holdPayout,
  listEligiblePayouts,
  listPayouts,
  mockMarkPaid,
  payNow,
  schedulePayout,
  unholdPayout,
} from '../../api/adminPayoutsApi';
import { useCurrency } from '../../components/CurrencyContext';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ON_HOLD', label: 'On hold' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'PAID', label: 'Paid' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const badgeStyles = {
  PENDING: 'bg-amber-50 text-amber-700 ring-amber-100',
  ON_HOLD: 'bg-rose-50 text-rose-700 ring-rose-100',
  SCHEDULED: 'bg-sky-50 text-sky-700 ring-sky-100',
  PROCESSING: 'bg-indigo-50 text-indigo-700 ring-indigo-100',
  PAID: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  FAILED: 'bg-rose-50 text-rose-700 ring-rose-100',
  CANCELLED: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const statusLabel = (value) => String(value || 'unknown').replace(/_/g, ' ').toLowerCase();

const PayoutBadge = ({ status }) => {
  const key = String(status || '').toUpperCase();
  const cls = badgeStyles[key] || 'bg-slate-100 text-slate-700 ring-slate-200';
  return <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>{statusLabel(key)}</span>;
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const toDateTimeInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`;
};

const AdminPayouts = ({ token }) => {
  const { formatCurrency } = useCurrency();
  const [filters, setFilters] = useState({ status: '', search: '', from: '', to: '' });
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const formatMoney = (currency, amount) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return 'N/A';
    const value = numeric / 100;
    return formatCurrency(value, currency || 'INR') || `${currency || 'INR'} ${value}`;
  };
  const resolveAmount = (row) => (row?.amountPaise ?? row?.amount);

  const [createForm, setCreateForm] = useState({ paymentId: '', amount: '' });
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [eligibleItems, setEligibleItems] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [eligibleError, setEligibleError] = useState('');
  const [eligibleQuery, setEligibleQuery] = useState('');
  const [selectedEligible, setSelectedEligible] = useState({});
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState('');
  const [rowActionValue, setRowActionValue] = useState({});
  const [rowActionLoading, setRowActionLoading] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState('');
  const [drawerData, setDrawerData] = useState(null);
  const [scheduleValue, setScheduleValue] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [holdReason, setHoldReason] = useState('');

  const load = async (page = pagination.page || 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await listPayouts(token, { ...filters, page, limit: pagination.limit });
      setItems(res.items || []);
      setPagination({ page: res.page || page, limit: res.limit || pagination.limit, total: res.total || 0 });
    } catch (err) {
      setError(err.message || 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (id) => {
    setDrawerLoading(true);
    setDrawerError('');
    try {
      const res = await getPayout(token, id);
      setDrawerData(res);
      setScheduleValue(toDateTimeInput(res?.payout?.scheduledFor || res?.payout?.scheduled_for));
      setHoldReason(res?.payout?.holdReason || res?.payout?.hold_reason || '');
    } catch (err) {
      setDrawerError(err.message || 'Failed to load payout');
    } finally {
      setDrawerLoading(false);
    }
  };

  const openDrawer = async (payout) => {
    setDrawerOpen(true);
    setSelectedId(payout.id);
    await fetchDetail(payout.id);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerData(null);
    setDrawerError('');
    setSelectedId(null);
    setScheduleValue('');
    setActionLoading('');
    setHoldReason('');
  };

  const refreshDetail = async () => {
    if (!selectedId) return;
    await fetchDetail(selectedId);
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status]);

  useEffect(() => {
    loadEligible('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => {
    load(1);
  };

  const handleCreate = async () => {
    setCreateError('');
    const paymentId = Number(createForm.paymentId);
    const amountValue = Number(createForm.amount);
    if (!paymentId) {
      setCreateError('Payment ID is required.');
      return;
    }
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      setCreateError('Amount must be greater than 0.');
      return;
    }
    setCreateLoading(true);
    try {
      const amountPaise = Math.round(amountValue * 100);
      await createPayout(token, { paymentId, amount: amountPaise });
      setCreateForm({ paymentId: '', amount: '' });
      await load(1);
    } catch (err) {
      setCreateError(err.message || 'Failed to create payout');
    } finally {
      setCreateLoading(false);
    }
  };

  const loadEligible = async (query = eligibleQuery) => {
    setEligibleLoading(true);
    setEligibleError('');
    try {
      const res = await listEligiblePayouts(token, { q: query, limit: 20 });
      setEligibleItems(res.items || []);
      setSelectedEligible({});
    } catch (err) {
      setEligibleError(err.message || 'Failed to load eligible payables');
    } finally {
      setEligibleLoading(false);
    }
  };

  const toggleEligible = (paymentId) => {
    setSelectedEligible((prev) => ({ ...prev, [paymentId]: !prev[paymentId] }));
  };

  const toggleAllEligible = () => {
    if (!eligibleItems.length) return;
    if (allEligibleSelected) {
      setSelectedEligible({});
      return;
    }
    const next = {};
    eligibleItems.forEach((item) => {
      next[item.paymentId] = true;
    });
    setSelectedEligible(next);
  };

  const handleBatchCreate = async () => {
    setBatchError('');
    const selected = eligibleItems.filter((item) => selectedEligible[item.paymentId]);
    if (!selected.length) {
      setBatchError('Select at least one payable.');
      return;
    }
    setBatchLoading(true);
    try {
      const payload = {
        payouts: selected.map((item) => ({
          paymentId: item.paymentId,
          amount: item.availableAmountPaise,
        })),
      };
      const res = await createPayoutBatch(token, payload);
      if (res?.failed?.length) {
        setBatchError(`Created ${res.created?.length || 0} payouts. ${res.failed.length} failed.`);
      }
      await Promise.all([load(1), loadEligible(eligibleQuery)]);
    } catch (err) {
      setBatchError(err.message || 'Failed to create batch payouts');
    } finally {
      setBatchLoading(false);
    }
  };

  const selectedEligibleItems = useMemo(() => {
    return eligibleItems.filter((item) => selectedEligible[item.paymentId]);
  }, [eligibleItems, selectedEligible]);

  const selectedEligibleTotal = useMemo(() => {
    return selectedEligibleItems.reduce((sum, item) => sum + Number(item.availableAmountPaise || 0), 0);
  }, [selectedEligibleItems]);

  const allEligibleSelected = useMemo(() => {
    return eligibleItems.length > 0 && eligibleItems.every((item) => selectedEligible[item.paymentId]);
  }, [eligibleItems, selectedEligible]);
  const selectedEligibleCurrency = selectedEligibleItems[0]?.currency || 'INR';

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 20)));
  }, [pagination]);

  const payout = drawerData?.payout || null;
  const account = drawerData?.account || null;
  const host = payout?.host || {};
  const payment = payout?.payment || null;
  const split = payment?.split || null;
  const hostName = host?.name || host?.full_name || payout?.host_name || 'Host';
  const hostEmail = host?.email || host?.phone || payout?.host_email || '';
  const payoutRef = payout?.payoutRef || payout?.reference || (payout?.id ? `#${payout.id}` : 'Payout detail');
  const payoutCreatedAt = payout?.createdAt || payout?.created_at;
  const payoutScheduledFor = payout?.scheduledFor || payout?.scheduled_for;
  const payoutPaidAt = payout?.paidAt || payout?.paid_at;
  const payoutBookingId = payout?.bookingId || payout?.booking_id;
  const payoutPaymentId = payout?.paymentId || payout?.payment_id;
  const payoutFailureReason = payout?.failureReason || payout?.failure_reason;
  const payoutHoldReason = payout?.holdReason || payout?.hold_reason;
  const payoutGatewayTransferId = payout?.gatewayTransferId || payout?.gateway_transfer_id;

  const payoutStatus = String(payout?.status || '').toUpperCase();
  const canHold = payout && !['PAID', 'FAILED', 'CANCELLED', 'ON_HOLD'].includes(payoutStatus);
  const canUnhold = payoutStatus === 'ON_HOLD';
  const canSchedule = payout && !['PAID', 'FAILED', 'CANCELLED'].includes(payoutStatus);
  const canPayNow = payout && !['PAID', 'FAILED', 'CANCELLED', 'ON_HOLD'].includes(payoutStatus);
  const canMockPaid = payout && !['PAID', 'FAILED', 'CANCELLED'].includes(payoutStatus);

  const handleHold = async () => {
    if (!selectedId) return;
    const reason = holdReason.trim();
    if (!reason) {
      setDrawerError('Provide a hold reason before placing a payout on hold.');
      return;
    }
    setActionLoading('hold');
    setDrawerError('');
    try {
      await holdPayout(token, selectedId, { holdReason: reason });
      await Promise.all([refreshDetail(), load(pagination.page || 1)]);
    } catch (err) {
      setDrawerError(err.message || 'Failed to hold payout');
    } finally {
      setActionLoading('');
    }
  };

  const handleUnhold = async () => {
    if (!selectedId) return;
    setActionLoading('unhold');
    setDrawerError('');
    try {
      await unholdPayout(token, selectedId);
      await Promise.all([refreshDetail(), load(pagination.page || 1)]);
    } catch (err) {
      setDrawerError(err.message || 'Failed to unhold payout');
    } finally {
      setActionLoading('');
    }
  };

  const handleSchedule = async () => {
    if (!selectedId) return;
    if (!scheduleValue) {
      setDrawerError('Select a schedule date and time.');
      return;
    }
    setActionLoading('schedule');
    setDrawerError('');
    try {
      await schedulePayout(token, selectedId, { scheduledFor: scheduleValue });
      await Promise.all([refreshDetail(), load(pagination.page || 1)]);
    } catch (err) {
      setDrawerError(err.message || 'Failed to schedule payout');
    } finally {
      setActionLoading('');
    }
  };

  const handlePayNow = async () => {
    if (!selectedId) return;
    setActionLoading('pay');
    setDrawerError('');
    try {
      await payNow(token, selectedId);
      await Promise.all([refreshDetail(), load(pagination.page || 1)]);
    } catch (err) {
      setDrawerError(err.message || 'Failed to process payout');
    } finally {
      setActionLoading('');
    }
  };

  const handleMockMarkPaid = async () => {
    if (!selectedId) return;
    setActionLoading('paid');
    setDrawerError('');
    try {
      await mockMarkPaid(token, selectedId);
      await Promise.all([refreshDetail(), load(pagination.page || 1)]);
    } catch (err) {
      setDrawerError(err.message || 'Failed to mark payout paid');
    } finally {
      setActionLoading('');
    }
  };

  const resetRowAction = (id) => {
    setRowActionValue((prev) => ({ ...prev, [id]: '' }));
  };

  const handleRowAction = async (payout, action) => {
    if (!action || !payout?.id) return;
    if (action === 'view') {
      resetRowAction(payout.id);
      openDrawer(payout);
      return;
    }

    let holdReasonValue = '';
    let scheduledForValue = '';
    if (action === 'hold') {
      holdReasonValue = window.prompt('Hold reason for this payout:') || '';
      if (!holdReasonValue.trim()) {
        resetRowAction(payout.id);
        return;
      }
    }
    if (action === 'schedule') {
      scheduledForValue =
        window.prompt('Schedule for (YYYY-MM-DDTHH:mm):', toDateTimeInput(new Date())) || '';
      if (!scheduledForValue.trim()) {
        resetRowAction(payout.id);
        return;
      }
    }

    setRowActionLoading(payout.id);
    setError('');
    try {
      if (action === 'hold') {
        await holdPayout(token, payout.id, { holdReason: holdReasonValue });
      } else if (action === 'unhold') {
        await unholdPayout(token, payout.id);
      } else if (action === 'schedule') {
        await schedulePayout(token, payout.id, { scheduledFor: scheduledForValue });
      } else if (action === 'pay_now') {
        await payNow(token, payout.id);
      } else if (action === 'mark_paid') {
        await mockMarkPaid(token, payout.id);
      }
      const refresh = selectedId === payout.id ? refreshDetail() : Promise.resolve();
      await Promise.all([load(pagination.page || 1), refresh]);
    } catch (err) {
      setError(err.message || 'Failed to update payout');
    } finally {
      setRowActionLoading(null);
      resetRowAction(payout.id);
    }
  };

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Finance</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Host Payouts</h1>
          <p className="text-sm text-slate-600">Monitor and act on host payouts.</p>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Create payout</p>
            <p className="text-sm text-slate-700">Create a payout against a captured payment.</p>
          </div>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <input
            type="number"
            min="1"
            placeholder="Payment ID"
            value={createForm.paymentId}
            onChange={(e) => setCreateForm((f) => ({ ...f, paymentId: e.target.value }))}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
          />
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="Amount (INR)"
            value={createForm.amount}
            onChange={(e) => setCreateForm((f) => ({ ...f, amount: e.target.value }))}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={createLoading}
            className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
          >
            {createLoading ? 'Creating...' : 'Create payout'}
          </button>
        </div>
        {createError && <p className="mt-2 text-sm text-rose-600">{createError}</p>}
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Batch payouts</p>
            <p className="text-sm text-slate-700">Select eligible payables and create payouts in bulk.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              placeholder="Search host, guest, or booking"
              value={eligibleQuery}
              onChange={(e) => setEligibleQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadEligible(e.target.value);
              }}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => loadEligible(eligibleQuery)}
              disabled={eligibleLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {eligibleLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allEligibleSelected}
                    onChange={toggleAllEligible}
                    aria-label="Select all eligible payables"
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                </th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Host</th>
                <th className="px-4 py-3">Available</th>
                <th className="px-4 py-3">Paid out</th>
                <th className="px-4 py-3">Gateway</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {eligibleLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-slate-500">
                    Loading eligible payables...
                  </td>
                </tr>
              )}
              {eligibleError && !eligibleLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-rose-600">
                    {eligibleError}
                  </td>
                </tr>
              )}
              {!eligibleLoading && !eligibleItems.length && !eligibleError && (
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-slate-500">
                    No eligible payables found.
                  </td>
                </tr>
              )}
              {eligibleItems.map((item) => {
                const paymentRef = item.paymentRef || `#${item.paymentId}`;
                const bookingRef = item.bookingRef;
                const hostName = item.host?.name || 'Host';
                const hostEmail = item.host?.email || 'N/A';
                const guestName = item.guest?.name || 'Guest';
                const guestEmail = item.guest?.email || '';
                const gatewayLabel = item.gatewayProvider || 'N/A';
                const gatewayPaymentId = item.gatewayPaymentId || null;
                const gatewayOrderId = item.gatewayOrderId || null;
                return (
                  <tr key={item.paymentId} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={Boolean(selectedEligible[item.paymentId])}
                        onChange={() => toggleEligible(item.paymentId)}
                        aria-label={`Select payment ${paymentRef}`}
                        className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold text-slate-900">{paymentRef}</div>
                      <div className="text-xs text-slate-500">
                        {bookingRef ? `Booking ${bookingRef}` : `Payment #${item.paymentId}`}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="font-semibold text-slate-900">{hostName}</div>
                      <div className="text-xs text-slate-500">{hostEmail}</div>
                      <div className="text-xs text-slate-400">
                        {guestName} {guestEmail ? `(${guestEmail})` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">
                      {formatMoney(item.currency, item.availableAmountPaise)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatMoney(item.currency, item.paidOutPaise)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      <div className="text-sm font-semibold text-slate-900">{gatewayLabel}</div>
                      <div className="text-xs text-slate-500">{gatewayPaymentId || 'N/A'}</div>
                      {gatewayOrderId && <div className="text-xs text-slate-500">Order {gatewayOrderId}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(item.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-slate-600">
            {selectedEligibleItems.length
              ? `${selectedEligibleItems.length} selected - Total ${formatMoney(
                  selectedEligibleCurrency,
                  selectedEligibleTotal
                )}`
              : 'No payables selected.'}
          </div>
          <button
            type="button"
            onClick={handleBatchCreate}
            disabled={batchLoading || !selectedEligibleItems.length}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
          >
            {batchLoading ? 'Creating...' : 'Create batch payouts'}
          </button>
        </div>
        {batchError && <p className="mt-2 text-sm text-rose-600">{batchError}</p>}
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-5">
        <select
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search reference or host"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') applyFilters();
          }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Apply filters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Payout</th>
              <th className="px-4 py-3">Host</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Scheduled</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-rose-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !items.length && !error && (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-slate-500">
                  No payouts found.
                </td>
              </tr>
            )}
            {items.map((p) => {
              const hostName = p.host?.name || p.host?.full_name || p.host_name || 'Host';
              const hostEmail = p.host?.email || p.host?.phone || p.host_email || 'N/A';
              const payoutRef = p.payoutRef || p.reference || `#${p.id}`;
              const createdAt = p.createdAt || p.created_at;
              const scheduledFor = p.scheduledFor || p.scheduled_for;
              const paymentRef = p.paymentId || p.payment_id;
              const rowStatus = String(p.status || '').toUpperCase();
              const rowCanHold = !['PAID', 'FAILED', 'CANCELLED', 'ON_HOLD'].includes(rowStatus);
              const rowCanUnhold = rowStatus === 'ON_HOLD';
              const rowCanSchedule = !['PAID', 'FAILED', 'CANCELLED'].includes(rowStatus);
              const rowCanPayNow = !['PAID', 'FAILED', 'CANCELLED', 'ON_HOLD'].includes(rowStatus);
              const rowCanMarkPaid = !['PAID', 'FAILED', 'CANCELLED'].includes(rowStatus);
              return (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-slate-900">{payoutRef}</div>
                    <div className="text-xs text-slate-500">{paymentRef ? `Payment ${paymentRef}` : 'Manual payout'}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div className="font-semibold text-slate-900">{hostName}</div>
                    <div className="text-xs text-slate-500">{hostEmail}</div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{formatMoney(p.currency, resolveAmount(p))}</td>
                  <td className="px-4 py-3">
                    <PayoutBadge status={p.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(scheduledFor)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDateTime(createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        onClick={() => openDrawer(p)}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        View
                      </button>
                      <select
                        value={rowActionValue[p.id] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRowActionValue((prev) => ({ ...prev, [p.id]: value }));
                          handleRowAction(p, value);
                        }}
                        disabled={rowActionLoading === p.id}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 focus:border-emerald-500 focus:outline-none"
                      >
                        <option value="">Quick actions</option>
                        <option value="view">View details</option>
                        <option value="pay_now" disabled={!rowCanPayNow}>
                          Pay now
                        </option>
                        <option value="hold" disabled={!rowCanHold}>
                          Hold
                        </option>
                        <option value="unhold" disabled={!rowCanUnhold}>
                          Unhold
                        </option>
                        <option value="schedule" disabled={!rowCanSchedule}>
                          Schedule
                        </option>
                        <option value="mark_paid" disabled={!rowCanMarkPaid}>
                          Mark paid (testing)
                        </option>
                      </select>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {pagination.page || 1} of {pageCount}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(Math.max(1, (pagination.page || 1) - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) <= 1}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => load((pagination.page || 1) + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) >= pageCount}
          >
            Next
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={closeDrawer}
            role="button"
            tabIndex={-1}
            aria-label="Close payout drawer"
          />
          <div className="relative ml-auto flex h-full w-full max-w-xl flex-col bg-white shadow-xl">
            <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-emerald-700">Payout</p>
                <h2 className="text-lg font-semibold text-slate-900">{payoutRef}</h2>
                <p className="text-sm text-slate-600">
                  {hostName} {hostEmail ? `(${hostEmail})` : ''}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <PayoutBadge status={payout?.status} />
                <button
                  type="button"
                  onClick={closeDrawer}
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {drawerLoading && <p className="text-sm text-slate-500">Loading payout details...</p>}
              {drawerError && !drawerLoading && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {drawerError}
                </div>
              )}
              {!drawerLoading && !drawerError && !payout && (
                <p className="text-sm text-slate-500">No payout details available.</p>
              )}
              {!drawerLoading && !drawerError && payout && (
                <div className="space-y-5">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Amount</p>
                      <p className="text-lg font-semibold text-slate-900">{formatMoney(payout.currency, resolveAmount(payout))}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Created</p>
                      <p className="text-sm font-semibold text-slate-900">{formatDateTime(payoutCreatedAt)}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Scheduled</p>
                      <p className="text-sm font-semibold text-slate-900">{formatDateTime(payoutScheduledFor)}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Paid</p>
                      <p className="text-sm font-semibold text-slate-900">{formatDateTime(payoutPaidAt)}</p>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-sm font-semibold text-slate-900">Host details</p>
                    <div className="mt-2 text-sm text-slate-700">
                      <p>{hostName}</p>
                      <p>{hostEmail || 'N/A'}</p>
                      <p className="text-xs text-slate-500">Host ID: {payout.hostId}</p>
                    </div>
                  </div>

                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-sm font-semibold text-slate-900">Payment context</p>
                    <div className="mt-2 grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Payment</p>
                        <p>{payment?.paymentRef || payoutPaymentId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Gateway transfer</p>
                        <p>{payoutGatewayTransferId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Booking</p>
                        <p>{payoutBookingId || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Payment amount</p>
                        <p>{payment ? formatMoney(payment.currency, payment.amountPaise ?? payment.amount) : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-slate-500">Payment status</p>
                        <p>{payment?.status || 'N/A'}</p>
                      </div>
                    </div>
                    {split && (
                      <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Split</p>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <div>Host gross: {formatMoney(payment?.currency, split.hostGross)}</div>
                          <div>Platform fee: {formatMoney(payment?.currency, split.platformFee)}</div>
                          <div>Gateway fee: {formatMoney(payment?.currency, split.gatewayFee)}</div>
                          <div>Host net: {formatMoney(payment?.currency, split.hostNet)}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-sm font-semibold text-slate-900">Bank account</p>
                    {account ? (
                      <div className="mt-2 text-sm text-slate-700">
                        <p>Status: {account.status}</p>
                        <p>
                          {account.accountHolder} - {account.bankName}
                        </p>
                        <p className="text-xs text-slate-500">Account: {account.accountNumber}</p>
                      </div>
                    ) : (
                      <p className="mt-2 text-sm text-slate-500">No bank account on file.</p>
                    )}
                  </div>

                  {payoutHoldReason && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                      Hold reason: {payoutHoldReason}
                    </div>
                  )}
                  {payoutFailureReason && (
                    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                      Failure reason: {payoutFailureReason}
                    </div>
                  )}

                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-sm font-semibold text-slate-900">Actions</p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <input
                          type="text"
                          value={holdReason}
                          onChange={(e) => setHoldReason(e.target.value)}
                          placeholder="Hold reason (required to hold)"
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleHold}
                        disabled={!canHold || actionLoading}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        {actionLoading === 'hold' ? 'Holding...' : 'Hold'}
                      </button>
                      <button
                        type="button"
                        onClick={handleUnhold}
                        disabled={!canUnhold || actionLoading}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        {actionLoading === 'unhold' ? 'Unholding...' : 'Unhold'}
                      </button>
                      <div className="sm:col-span-2 flex flex-col gap-2 sm:flex-row">
                        <input
                          type="datetime-local"
                          value={scheduleValue}
                          onChange={(e) => setScheduleValue(e.target.value)}
                          className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={handleSchedule}
                          disabled={!canSchedule || actionLoading}
                          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {actionLoading === 'schedule' ? 'Scheduling...' : 'Schedule'}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={handlePayNow}
                        disabled={!canPayNow || actionLoading}
                        className="sm:col-span-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {actionLoading === 'pay' ? 'Paying...' : 'Pay now (mock gateway)'}
                      </button>
                      <button
                        type="button"
                        onClick={handleMockMarkPaid}
                        disabled={!canMockPaid || actionLoading}
                        className="sm:col-span-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                      >
                        {actionLoading === 'paid' ? 'Marking paid...' : 'Mark paid (testing)'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayouts;
